import { useEffect, useRef } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import useAuth from "./useAuth";
import { useSubscriptionQuery } from "./subscription/useSubscriptionQuery";
import { identifyUser } from "@/utils/gtm";

/**
 * Identifies the authenticated user to GTM/GA4 once per session.
 *
 * - For chargebee users: waits for subscription data before firing so that
 *   subscription_status, plan, age, and trial fields are included.
 * - For all other users (corporate/direct/internal): fires immediately.
 */
export const useUserIdentification = () => {
  const { authenticated, keycloakInstance, tokenParsed } = useAuth();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionQuery();
  const hasFired = useRef(false);

  useEffect(() => {
    if (!authenticated || !tokenParsed || hasFired.current) return;

    const tp = tokenParsed as KeycloakTokenParsed & {
      access_type?: string;
      email?: string;
    };
    const accessType = tp.access_type ?? "unknown";

    // Chargebee users: wait for subscription data before firing
    if (accessType === "chargebee" && isSubscriptionLoading) return;

    hasFired.current = true;

    const appRoles = tp.resource_access?.app?.roles ?? [];
    const clientId = keycloakInstance?.clientId;
    const clientRoles = clientId
      ? tp.resource_access?.[clientId]?.roles ?? []
      : [];

    const email = tp.email;
    const isInternal =
      (email?.endsWith("@newsquawk.com") && !email?.includes("+external")) ||
      clientRoles.includes("internal-newsquawk-user");

    const userType = isInternal ? "internal" : accessType;

    const subscriptionAgeDays = subscription?.created_at
      ? Math.floor(
          (Date.now() - new Date(subscription.created_at).getTime()) / 86400000
        )
      : null;

    const trialDaysRemaining = subscription?.trial_end
      ? Math.max(
          0,
          Math.floor(
            (new Date(subscription.trial_end).getTime() - Date.now()) / 86400000
          )
        )
      : null;

    identifyUser({
      user_id: tp.sub ?? "unknown",
      access_type: accessType,
      user_type: userType,
      subscription_status: subscription?.status,
      subscription_plan: subscription?.plan_id,
      subscription_age_days: subscriptionAgeDays,
      trial_days_remaining: trialDaysRemaining,
      squawk_channels: appRoles
        .filter((r) => r.startsWith("squawk-"))
        .sort()
        .join(","),
      has_app_pro: appRoles.includes("app-pro"),
      has_equities_suite: appRoles.includes("equities-suite-widgets"),
    });
  }, [
    authenticated,
    tokenParsed,
    keycloakInstance,
    subscription,
    isSubscriptionLoading,
  ]);
};
